/**
 * Users Admin API client(v5 §13 / Sprint 10)— FE Implementer。
 *
 * 严格对齐 backend_app/app/schemas/user_admin.py:
 *   UserOut(id, username, role, tenant_id)— Sprint 10 不接 status/last_login_at
 *   UserPatch(role 可选)
 *   UserResetPasswordRequest(空 body)
 *
 * 删除前推荐先调 `GET /admin/accounts/{id}/delete-impact-preview`(已有,见
 * admin_accounts_extras router)展示影响范围;UsersAdminPage 实现里二次确认弹窗。
 */
import { z } from 'zod';
import { apiGetParsed, apiPatchParsed, apiPostParsed, apiDelete } from './client';
import { paginationSchema } from './_types/common';
import type { Pagination } from './_types/common';

// ============================================================================
// schemas
// ============================================================================

export const userSchema = z.object({
  id: z.string().uuid(),
  username: z.string(), // = email
  role: z.string(),
  tenant_id: z.string().uuid().nullable(),
});
export type User = z.infer<typeof userSchema>;

export const userListSchema = paginationSchema(userSchema);
export type UserList = z.infer<typeof userListSchema>;

// 复用 admin_accounts_extras 的预览(DeleteImpactPreview)— 删除前 UI 二次确认
export const enterpriseRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  tenant_id: z.string().uuid(),
});

export const investmentRefSchema = z.object({
  id: z.string().uuid(),
  enterprise_id: z.string().uuid(),
  amount: z.number(),
  tenant_id: z.string().uuid(),
});

export const deleteImpactPreviewSchema = z.object({
  account_id: z.string().uuid(),
  enterprises_to_orphan: z.array(enterpriseRefSchema),
  investments_to_orphan: z.array(investmentRefSchema),
  total_orphan_count: z.number().int().nonnegative(),
});
export type DeleteImpactPreview = z.infer<typeof deleteImpactPreviewSchema>;

// ============================================================================
// 入参 types
// ============================================================================

export interface UserPatchBody {
  role?: string;
}

export interface ListUsersParams {
  role?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

// ============================================================================
// endpoint
// ============================================================================

export async function listUsers(
  params: ListUsersParams = {},
): Promise<Pagination<User>> {
  return apiGetParsed('/users', userListSchema, params as Record<string, unknown>);
}

export async function patchUser(id: string, body: UserPatchBody): Promise<User> {
  return apiPatchParsed(`/users/${encodeURIComponent(id)}`, userSchema, body);
}

export async function deleteUser(id: string): Promise<unknown> {
  return apiDelete(`/users/${encodeURIComponent(id)}`);
}

/** 触发重置密码邀请 — 后端起一条 invites 行,发邮件。 */
export async function resetUserPassword(id: string): Promise<unknown> {
  return apiPostParsed(
    `/users/${encodeURIComponent(id)}/reset-password`,
    z.unknown(),
    {},
  );
}

/**
 * 删用户前的影响范围预览。复用 R3-Sprint6 已有 endpoint:
 *   GET /admin/accounts/{account_id}/delete-impact-preview
 * 返:enterprises_to_orphan / investments_to_orphan / total_orphan_count
 */
export async function getDeleteImpactPreview(
  accountId: string,
): Promise<DeleteImpactPreview> {
  return apiGetParsed(
    `/admin/accounts/${encodeURIComponent(accountId)}/delete-impact-preview`,
    deleteImpactPreviewSchema,
  );
}
